import React, { useEffect, useState } from "react";
import { IntlShape } from "react-intl";
import { FieldAttributes } from "formik";
import { match as Match } from "react-router-dom";
import { toast } from "react-toastify";
import Block from "../../../../../components/Tailwind/Block";
import Header from "../../../../../components/Tailwind/Block/Header";
import DefaultForm, {
  AdditionalFieldAttributes,
} from "../../../../../components/Form";
import overrideClasses from "../../../../../utils/overrideClasses";
import { GroundGraphqlContextStore } from "../../../../../lib/ground-aws-graphql-core";
import { isString } from "../../../../../utils/types";
import history from "../../../../../history";
import { EnumPaths } from "../../../../../utils/navigation";
import { EnumPermissionEntity } from "../../../../../lib/ground-aws-graphql-core/api/graphql/types";
import ListCheckpoints from "../checkpoints";

interface Props {
  match: Match<{ cid: string; id?: string }>;
  intl: IntlShape;
  edition: boolean;
}

const CheckpointTypeForm = (props: Props): JSX.Element => {
  const { intl, match, edition } = props;

  const [enabled, setEnabled] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [togglePanel, setTogglePanel] = useState(false);

  const getCheckpointType = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.getCheckpointType
  );

  const createCheckpointType = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.createCheckpointType
  );

  const updateCheckpointType = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.updateCheckpointType
  );

  const setCheckpointType = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.setCheckpointType
  );

  const checkpointType = GroundGraphqlContextStore.useStoreState(
    state => state.inspection.checkpointType
  );

  const formFields: FieldAttributes<AdditionalFieldAttributes>[] = [
    {
      name: "title",
      label: "page.inspection.type.label",
      placeholder: "page.inspection.type.label.placeholder",
      initialValue: checkpointType?.title,
      required: true,
      translatable: true,
    },
  ];

  const fetchData = () => {
    setLoading(true);
    if (match?.params.id) {
      getCheckpointType({ id: match?.params.id })
        .catch(() => {
          toast(
            intl.formatMessage({
              id: "page.inspection.type.get.error",
            }),
            {
              type: "error",
            }
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Clean the event from the state when we unmount this component
    return () => {
      setCheckpointType(null);
    };
  }, []);

  useEffect(() => {
    if (checkpointType) setEnabled(checkpointType.enabled);
  }, [checkpointType]);

  const handleShowSavePanel = () => {
    setTogglePanel(true);
  };

  const handleSubmit = (values: any) => {
    setLoading(true);
    const { title, ...others } = values;
    if (edition && checkpointType) {
      updateCheckpointType({
        ...others,
        title: isString(title) ? JSON.parse(title) : title,
        id: checkpointType.id,
        enabled,
      })
        .then(() => {
          history.push(
            `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.CHECKPOINT_TYPES}`
          );
          toast(
            intl.formatMessage({
              id: "page.inspection.type.update.success",
            }),
            { type: "success" }
          );
        })
        .catch(() => {
          history.push(
            `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.CHECKPOINT_TYPES}`
          );
          toast(
            intl.formatMessage({
              id: "page.inspection.type.update.error",
            }),
            {
              type: "error",
            }
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      createCheckpointType({
        ...others,
        title: isString(title) ? JSON.parse(title) : title,
        building_id: match.params.cid,
        enabled,
      })
        .then(() => {
          history.push(
            `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.CHECKPOINT_TYPES}`
          );
          toast(
            intl.formatMessage({ id: "page.inspection.type.create.success" }),
            { type: "success" }
          );
        })
        .catch(() => {
          history.push(
            `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.CHECKPOINT_TYPES}`
          );
          toast(
            intl.formatMessage({ id: "page.inspection.type.create.error" }),
            {
              type: "error",
            }
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return loading ? (
    <div className="loading" />
  ) : (
    <>
      {/**
       * When we were loading the form was initialized again and we would lose all data
       * Show loading here in order to not lose the fields data after a loading
       */}
      <div className={overrideClasses({ loading })} />

      {/* Hide when loading */}
      <div className={overrideClasses("px-8 pb-32", { hidden: loading })}>
        <Block>
          <Header
            item={checkpointType}
            checked={enabled}
            title={
              edition
                ? "page.list.inspection.types.update.checkpoint.type"
                : "page.list.inspection.types.create.checkpoint.type"
            }
            entity={EnumPermissionEntity.CHECKPOINT_TYPE}
            onChange={e => {
              setEnabled(e);

              // show save panel
              handleShowSavePanel();
            }}
            className="border-b border-gray-200"
          />

          <DefaultForm
            fields={formFields}
            onSubmit={handleSubmit}
            showPanel={togglePanel}
          />
        </Block>
        {edition && match.params.id && (
          <ListCheckpoints typeId={match.params.id} {...props} />
        )}
      </div>
    </>
  );
};

export default CheckpointTypeForm;
